import React, { useState, useRef, useEffect, useCallback } from 'react';
import { ZoomIn, ZoomOut, RotateCcw, Move } from 'lucide-react';

const SeatMap = ({ 
  allSeats,
  plantaBajaSeats, 
  primerPisoSeats,
  handleSeatClick, 
  getSeatClassName,
  selectedSeats = [],
  reservationsToCancel = [],
  seatTypeList = [],
  seatTypePriceMap = {},
}) => {
  const [zoom, setZoom] = useState(0.65);
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const [position, setPosition] = useState({ x: 0, y: 130 });
  const [isMobile, setIsMobile] = useState(false);
  const [lastTouchDistance, setLastTouchDistance] = useState(0);
  const [lastTouchCenter, setLastTouchCenter] = useState({ x: 0, y: 0 });
  const [isZoomMode, setIsZoomMode] = useState(false);
  const [hoveredSeat, setHoveredSeat] = useState(null); // { seat, label, price, x, y }
  const containerRef = useRef(null);
  const mapRef = useRef(null);

  const seatStatusLabel = {
    available: 'Disponible',
    booked: 'Ocupado',
    sold: 'Vendido',
    reserved: 'Reservado',
    blocked: 'Bloqueado',
    selected: 'Seleccionado',
  };

  const handleSeatMouseEnter = useCallback((seat, label, price, e) => {
    if (isMobile) return;
    setHoveredSeat({ seat, label, price, x: e.clientX, y: e.clientY });
  }, [isMobile]);

  const handleSeatMouseMove = useCallback((e) => {
    if (isMobile) return;
    setHoveredSeat(prev => (prev ? { ...prev, x: e.clientX, y: e.clientY } : prev));
  }, [isMobile]);

  const handleSeatMouseLeave = useCallback(() => {
    setHoveredSeat(null);
  }, []);

  // Detectar si es dispositivo móvil
  useEffect(() => {
    const checkIsMobile = () => {
      const isMobileDevice = window.innerWidth <= 768 || 'ontouchstart' in window;
      setIsMobile(isMobileDevice);
      
      // Ajustar zoom inicial para móvil
      if (isMobileDevice && zoom === 0.65) {
        setZoom(0.4);
        setPosition({ x: 0, y: 80 });
      }
    };

    checkIsMobile();
    window.addEventListener('resize', checkIsMobile);
    return () => window.removeEventListener('resize', checkIsMobile);
  }, [zoom]);

 
  // Función para renderizar un asiento individual
  const renderSeat = (seat) => {
    // Verificar si el asiento está seleccionado por el usuario actual
    const isSelectedByUser = selectedSeats && selectedSeats.includes(seat.id);
    
    // Verificar si el asiento está marcado para cancelación (disponible para reseleccionar)
    const isMarkedForCancellation = reservationsToCancel.some(reservation => reservation.compositeId === seat.id);
    
    // Determinar el estilo inline para colores específicos
    let seatStyle = {};
    const seatClassName = getSeatClassName(seat);
    
    if (seatClassName.includes('style-sold')) {
      seatStyle.backgroundColor = 'rgb(22, 101, 52)';
    } else if (seatClassName.includes('style-reserved')) {
      seatStyle.backgroundColor = 'rgb(239, 246, 255)';
    } else if (seatClassName.includes('style-blocked')) {
      seatStyle.backgroundColor = 'rgb(254, 243, 199)';
    }
    
    return (
      <button
        key={seat.id}
        onClick={() => handleSeatClick(seat.id)}
        className={seatClassName.replace(' style-sold', '').replace(' style-reserved', '')}
        style={seatStyle}
        disabled={seat.status === "booked" && !isSelectedByUser && !isMarkedForCancellation}
        onMouseEnter={(e) => handleSeatMouseEnter(seat, `PB ${seat.number}`, seat.price, e)}
        onMouseMove={handleSeatMouseMove}
        onMouseLeave={handleSeatMouseLeave}
      >
      </button>
    );
  };

  // Función para renderizar un espacio vacío (no utilizable)
  const renderEmptySpace = (key) => (
    <div key={key} className="w-2.5 h-2.5 xs:w-3 xs:h-3 sm:w-3.5 sm:h-3.5 lg:w-4 lg:h-4 bg-transparent border border-gray-200 rounded-full opacity-30"></div>
  );

  // Función para renderizar un pasillo
  const renderPasillo = (key, rowNumber = null) => (
    <div key={key} className="w-6 h-3 xs:w-7 h-3.5 sm:w-8 h-4 lg:w-10 h-5 flex items-center justify-center">
      {rowNumber && (
        <span className="text-[8px] xs:text-[10px] sm:text-[12px] lg:text-[14px] font-bold text-gray-600">{rowNumber}</span>
      )}
    </div>
  );

  // Función para renderizar número de fila (solo para primer piso)
  const renderRowNumber = (rowNumber) => (
    <div className="w-6 h-3 xs:w-7 h-3.5 sm:w-8 h-4 lg:w-10 h-5 flex items-center justify-center">
      {rowNumber && (
        <span className="text-[8px] xs:text-[10px] sm:text-[12px] lg:text-[14px] font-bold text-gray-600">{rowNumber}</span>
      )}
    </div>
  );

  // Función para renderizar una fila completa con columnas fijas
  const renderRow = (rowNumber, sectionSeats, sectionType) => {
    const rowSeats = sectionSeats.filter(s => s.row === rowNumber);
    
    
    const sectionTypes = seatTypeList.filter(st => st.category === 'section');
    const rowRangeTypes = seatTypeList.filter(st => st.category === 'row_range');
    
    
    const getSeatTypeForSection = (section) => {
      const sectionType = sectionTypes.find(st => st.section === section);
      return sectionType || seatTypeList[0]; // Fallback al primer tipo disponible
    };
    
    
    const getSeatTypeForRow = (section, row) => {
      // Buscar tipos de rango de filas que coincidan con la fila actual
      const rowType = rowRangeTypes.find(st => 
        row >= st.startRow && 
        row <= st.endRow &&
        (st.section === section || st.section === '' || !st.section)
      );
      return rowType || getSeatTypeForSection(section);
    };
    
    // Definir las columnas fijas según el tipo de sección
    let columns;
    
    if (sectionType === "primer-piso") {
      
      const primerPisoType = getSeatTypeForSection('primer_piso');
      
      // Primer piso: sin pasillos, secuencia continua
      columns = [
        { number: 25, type: primerPisoType?.type || "primer_piso" },
        { number: 23, type: primerPisoType?.type || "primer_piso" },
        { number: 21, type: primerPisoType?.type || "primer_piso" },
        { number: 19, type: primerPisoType?.type || "primer_piso" },
        { number: 17, type: primerPisoType?.type || "primer_piso" },
        { number: 15, type: primerPisoType?.type || "primer_piso" },
        { number: 13, type: primerPisoType?.type || "primer_piso" },
        { number: 11, type: primerPisoType?.type || "primer_piso" },
        { number: 9, type: primerPisoType?.type || "primer_piso" },
        { number: 7, type: primerPisoType?.type || "primer_piso" },
        { number: 5, type: primerPisoType?.type || "primer_piso" },
        { number: 3, type: primerPisoType?.type || "primer_piso" },
        { number: 1, type: primerPisoType?.type || "primer_piso" },
        { number: 2, type: primerPisoType?.type || "primer_piso" },
        { number: 4, type: primerPisoType?.type || "primer_piso" },
        { number: 6, type: primerPisoType?.type || "primer_piso" },
        { number: 8, type: primerPisoType?.type || "primer_piso" },
        { number: 10, type: primerPisoType?.type || "primer_piso" },
        { number: 12, type: primerPisoType?.type || "primer_piso" },
        { number: 14, type: primerPisoType?.type || "primer_piso" },
        { number: 16, type: primerPisoType?.type || "primer_piso" },
        { number: 18, type: primerPisoType?.type || "primer_piso" },
        { number: 20, type: primerPisoType?.type || "primer_piso" },
        { number: 22, type: primerPisoType?.type || "primer_piso" },
        { number: 24, type: primerPisoType?.type || "primer_piso" },
        { number: 26, type: primerPisoType?.type || "primer_piso" },
      ];
    } else {
      
      const plantaBajaType = getSeatTypeForRow('planta_baja', rowNumber);
      const premiumType = getSeatTypeForSection('planta_baja'); // Para laterales
      
      // Planta Baja: con pasillos
      columns = [
        // Lado izquierdo (Premium) - usar tipo de sección completa
        { number: 29, type: premiumType?.type || "planta_baja" },
        { number: 27, type: premiumType?.type || "planta_baja" },
        { number: 25, type: premiumType?.type || "planta_baja" },
        { number: 23, type: premiumType?.type || "planta_baja" },
        { number: 21, type: premiumType?.type || "planta_baja" },
        { number: 19, type: premiumType?.type || "planta_baja" },
        { number: 17, type: premiumType?.type || "planta_baja" },
        { number: 15, type: premiumType?.type || "planta_baja" },
        
        // Pasillo izquierdo
        { number: null, type: "pasillo" },
        
        // Centro izquierdo - usar tipo dinámico por fila
        { number: 13, type: plantaBajaType?.type || "planta_baja" },
        { number: 11, type: plantaBajaType?.type || "planta_baja" },
        { number: 9, type: plantaBajaType?.type || "planta_baja" },
        { number: 7, type: plantaBajaType?.type || "planta_baja" },
        { number: 5, type: plantaBajaType?.type || "planta_baja" },
        { number: 3, type: plantaBajaType?.type || "planta_baja" },
        { number: 1, type: plantaBajaType?.type || "planta_baja" },
        
        // Centro derecho - usar tipo dinámico por fila
        { number: 2, type: plantaBajaType?.type || "planta_baja" },
        { number: 4, type: plantaBajaType?.type || "planta_baja" },
        { number: 6, type: plantaBajaType?.type || "planta_baja" },
        { number: 8, type: plantaBajaType?.type || "planta_baja" },
        { number: 10, type: plantaBajaType?.type || "planta_baja" },
        { number: 12, type: plantaBajaType?.type || "planta_baja" },
        { number: 14, type: plantaBajaType?.type || "planta_baja" },
        
        // Pasillo derecho
        { number: null, type: "pasillo" },
        
        // Lado derecho (Premium) - usar tipo de sección completa
        { number: 16, type: premiumType?.type || "planta_baja" },
        { number: 18, type: premiumType?.type || "planta_baja" },
        { number: 20, type: premiumType?.type || "planta_baja" },
        { number: 22, type: premiumType?.type || "planta_baja" },
        { number: 24, type: premiumType?.type || "planta_baja" },
        { number: 26, type: premiumType?.type || "planta_baja" },
        { number: 28, type: premiumType?.type || "planta_baja" },
        { number: 30, type: premiumType?.type || "planta_baja" },
      ];
    }

    // Función para verificar si una columna está disponible en esta fila
    const getRowAvailability = (row) => {
      if (sectionType === "planta-baja") {
        if (row === 1) return [false, false, false, false, true, true, true, true, true, true, true, true, true, true, true, true, true, true, true, true, true, true, true, true, true, true, true, true, false, false, false, false];
        if (row <= 4) return [false, false, false, true, true, true, true, true, true, true, true, true, true, true, true, true, true, true, true, true, true, true, true, true, true, true, true, true, true, false, false, false];
        if (row <= 9) return [false, false, true, true, true, true, true, true, true, true, true, true, true, true, true, true, true, true, true, true, true, true, true, true, true, true, true, true, true, true, false, false];
        if (row <= 14) return [false, true, true, true, true, true, true, true, true, true, true, true, true, true, true, true, true, true, true, true, true, true, true, true, true, true, true, true, true, true, true, false];
        if (row <= 16) return [true, true, true, true, true, true, true, true, true, true, true, true, true, true, true, true, true, true, true, true, true, true, true, true, true, true, true, true, true, true, true, true];
        if (row <= 20) return [false, true, true, true, true, true, true, true, true, true, true, true, true, true, true, true, true, true, true, true, true, true, true, true, true, true, true, true, true, true, true, false];
        if (row === 21) return [false, false, true, true, true, true, true, true, true, true, true, true, true, true, true, true, true, true, true, true, true, true, false, true, true, true, true, true, true, true, true, false];
        if (row === 22) return [false, false, true, true, true, true, true, true, true, false, true, true, true, true, true, true, true, true, true, true, true, true, false, true, true, true, true, true, true, true, true, false];
        if (row === 23) return [false, true, true, true, true, true, true, true, true, false, true, true, true, true, true, true, true, true, true, true, true, false, false, true, true, true, true, true, true, true, true, false];
        if (row === 24) return [false, false, true, true, true, true, true, true, false, false, false, false, false, false, false, false, false, false, false, false, false, false, false, true, true, true, true, true, true, true, false, false];
      } else if (sectionType === "primer-piso") {
        // Primer piso: todas las columnas disponibles (26 columnas sin pasillos)
        return [true, true, true, true, true, true, true, true, true, true, true, true, true, true, true, true, true, true, true, true, true, true, true, true, true, true];
      }
      return [];
    }

    const availability = getRowAvailability(rowNumber);

    return (
      <div key={`${sectionType}-row-${rowNumber}`} className="flex items-center gap-1">
        {/* Número de fila izquierdo - solo para primer piso */}
        {sectionType === "primer-piso" && renderRowNumber(rowNumber)}
        
        {columns.map((column, columnIndex) => {
          const isAvailable = availability[columnIndex];
          
          if (column.type === "pasillo") {
            return renderPasillo(`${sectionType}-pasillo-${rowNumber}-${columnIndex}`, rowNumber);
          }
          
          if (!isAvailable) {
            return renderEmptySpace(`${sectionType}-empty-${rowNumber}-${columnIndex}`);
          }
          
          // Buscar el asiento en esta posición
          const seat = rowSeats.find(s => s.number === column.number);
          
          if (seat) {
            return renderSeat(seat);
          } else {
            return renderEmptySpace(`${sectionType}-empty-${rowNumber}-${columnIndex}`);
          }
        })}
        
        {/* Número de fila derecho - solo para primer piso */}
        {sectionType === "primer-piso" && renderRowNumber(rowNumber)}
      </div>
    );
  };

  // Generar estructura de palcos izquierdos y derechos para Planta Baja, alineados con todas las filas
  const boxRows = [1, 3, 5, 7, 9, 11, 13, 15];
  
  const boxTypes = seatTypeList.filter(st => st.category === 'box' || st.type === 'palco');
  const palcoType = boxTypes[0] || seatTypeList.find(st => st.type === 'palco') || seatTypeList[0];
  const palcoPrice = seatTypePriceMap[palcoType?.type] || 0;

  // Utilidades para gestos táctiles
  const getTouchDistance = (touches) => {
    const dx = touches[0].clientX - touches[1].clientX;
    const dy = touches[0].clientY - touches[1].clientY;
    return Math.sqrt(dx * dx + dy * dy);
  };

  const getTouchCenter = (touches) => {
    return {
      x: (touches[0].clientX + touches[1].clientX) / 2,
      y: (touches[0].clientY + touches[1].clientY) / 2
    };
  };

  // Funciones de zoom y navegación
  const handleZoomIn = useCallback(() => {
    setZoom(prev => Math.min(prev + (isMobile ? 0.15 : 0.2), isMobile ? 2 : 3));
  }, [isMobile]);

  const handleZoomOut = useCallback(() => {
    setZoom(prev => Math.max(prev - (isMobile ? 0.15 : 0.2), isMobile ? 0.3 : 0.5));
  }, [isMobile]);

  const handleReset = useCallback(() => {
    const initialZoom = isMobile ? 0.4 : 0.65;
    const initialY = isMobile ? 80 : 130;
    setZoom(initialZoom);
    setPosition({ x: 0, y: initialY });
    setIsZoomMode(false);
  }, [isMobile]);

  // Manejadores de eventos de mouse (desktop)
  const handleMouseDown = useCallback((e) => {
    if (e.button === 0 && !isMobile) {
      setIsDragging(true);
      setDragStart({
        x: e.clientX - position.x,
        y: e.clientY - position.y
      });
    }
  }, [position, isMobile]);

  const handleMouseMove = useCallback((e) => {
    if (isDragging && !isMobile) {
      setPosition({
        x: e.clientX - dragStart.x,
        y: e.clientY - dragStart.y
      });
    }
  }, [isDragging, dragStart, isMobile]);

  const handleMouseUp = useCallback(() => {
    setIsDragging(false);
  }, []);

  const handleWheel = useCallback((e) => {
    if (!isMobile) {
      e.preventDefault();
      const delta = e.deltaY > 0 ? -0.1 : 0.1;
      setZoom(prev => Math.max(0.5, Math.min(3, prev + delta)));
    }
  }, [isMobile]);

  // Manejadores de eventos táctiles (mobile)
  const handleTouchStart = useCallback((e) => {
    if (!isMobile) return;
    
    e.preventDefault();
    const touches = e.touches;

    if (touches.length === 1) {
      // Un dedo: arrastre
      setIsDragging(true);
      setDragStart({
        x: touches[0].clientX - position.x,
        y: touches[0].clientY - position.y
      });
    } else if (touches.length === 2) {
      // Dos dedos: zoom
      setIsDragging(false);
      const distance = getTouchDistance(touches);
      const center = getTouchCenter(touches);
      setLastTouchDistance(distance);
      setLastTouchCenter(center);
    }
  }, [isMobile, position]);

  const handleTouchMove = useCallback((e) => {
    if (!isMobile) return;
    
    e.preventDefault();
    const touches = e.touches;

    if (touches.length === 1 && isDragging) {
      // Arrastre con un dedo
      setPosition({
        x: touches[0].clientX - dragStart.x,
        y: touches[0].clientY - dragStart.y
      });
    } else if (touches.length === 2 && lastTouchDistance > 0) {
      // Zoom con dos dedos
      const distance = getTouchDistance(touches);
      const center = getTouchCenter(touches);
      
      const scale = distance / lastTouchDistance;
      const newZoom = Math.max(0.3, Math.min(2, zoom * scale));
      
      // Ajustar posición para mantener el centro de zoom
      const deltaX = center.x - lastTouchCenter.x;
      const deltaY = center.y - lastTouchCenter.y;
      
      setZoom(newZoom);
      setPosition(prev => ({
        x: prev.x + deltaX * 0.5,
        y: prev.y + deltaY * 0.5
      }));
      
      setLastTouchDistance(distance);
      setLastTouchCenter(center);
    }
  }, [isMobile, isDragging, dragStart, lastTouchDistance, lastTouchCenter, zoom]);

  const handleTouchEnd = useCallback((e) => {
    if (!isMobile) return;
    
    setIsDragging(false);
    setLastTouchDistance(0);
    
    // Si quedan menos de 2 dedos, salir del modo zoom
    if (e.touches.length < 2) {
      setLastTouchDistance(0);
    }
  }, [isMobile]);

  return (
    <div className="w-full rounded-lg border bg-white text-gray-950 shadow-sm">
      {/* Header con controles */}
      <div className="flex flex-col space-y-1.5 p-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <h3 className="text-base sm:text-lg text-2xl font-semibold leading-none tracking-tight">
              Mapa del Teatro
            </h3>
          </div>
          
          {/* Controles de zoom */}
          <div className={`flex items-center gap-2 bg-gray-50 rounded-lg p-2 ${isMobile ? 'flex-col space-y-2' : ''}`}>
            {isMobile && (
              <div className="flex items-center gap-2 text-xs text-gray-600">
                <Move className="w-3 h-3" />
                <span>Arrastra con 1 dedo • Zoom con 2 dedos</span>
              </div>
            )}
            
            <div className={`flex items-center gap-2 ${isMobile ? 'w-full justify-center' : ''}`}>
              <button
                onClick={handleZoomOut}
                className={`p-2 rounded-md hover:bg-gray-200 transition-colors ${isMobile ? 'p-3' : ''}`}
                title="Alejar"
              >
                <ZoomOut className={`${isMobile ? 'w-5 h-5' : 'w-4 h-4'}`} />
              </button>
              
              <span className={`font-medium text-center ${isMobile ? 'text-base min-w-[4rem]' : 'text-sm min-w-[3rem]'}`}>
                {Math.round(zoom * 100)}%
              </span>
              
              <button
                onClick={handleZoomIn}
                className={`p-2 rounded-md hover:bg-gray-200 transition-colors ${isMobile ? 'p-3' : ''}`}
                title="Acercar"
              >
                <ZoomIn className={`${isMobile ? 'w-5 h-5' : 'w-4 h-4'}`} />
              </button>
              
              <button
                onClick={handleReset}
                className={`p-2 rounded-md hover:bg-gray-200 transition-colors ${isMobile ? 'p-3' : ''}`}
                title="Resetear vista"
              >
                <RotateCcw className={`${isMobile ? 'w-5 h-5' : 'w-4 h-4'}`} />
              </button>
            </div>
          </div>
        </div>
        <p className="text-xs sm:text-sm text-gray-500">
          Seleccioná los asientos disponibles para tu reserva
        </p>
      </div>

      {/* Contenedor del mapa con zoom */}
      <div className="p-6 pt-0 w-full">
        <div 
          ref={containerRef}
          className={`relative w-full overflow-hidden border rounded-lg bg-gray-50 ${isMobile ? 'h-[500px]' : 'h-[600px]'}`}
          onMouseDown={handleMouseDown}
          onMouseMove={handleMouseMove}
          onMouseUp={handleMouseUp}
          onMouseLeave={handleMouseUp}
          onWheel={handleWheel}
          onTouchStart={handleTouchStart}
          onTouchMove={handleTouchMove}
          onTouchEnd={handleTouchEnd}
          style={{ 
            cursor: isMobile ? 'auto' : (isDragging ? 'grabbing' : 'grab'),
            touchAction: 'none' // Prevenir scroll nativo en móvil
          }}
        >
          <div
            ref={mapRef}
            className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 transition-transform duration-200"
            style={{
              transform: `translate(-50%, -50%) translate(${position.x}px, ${position.y}px) scale(${zoom})`,
              transformOrigin: 'center center'
            }}
          >
            <div className="w-full max-w-none space-y-4 sm:space-y-6 lg:space-y-8">
              {/* ESCENARIO */}
              <div className="mb-4 sm:mb-6 lg:mb-8">
                <div className="flex justify-center">
                  <div className="bg-gradient-to-r from-gray-700 to-gray-800 text-white text-center py-3 sm:py-4 lg:py-5 rounded-lg px-6 sm:px-8 lg:px-10 w-full max-w-[calc(30*1.5rem+2rem)] sm:max-w-[calc(30*1.75rem+2rem)] lg:max-w-[calc(30*2.5rem+2rem)] shadow-lg">
                    <span className="text-sm sm:text-base lg:text-lg font-bold">ESCENARIO</span>
                  </div>
                </div>
              </div>
              {/* PALCOS + PLANTA BAJA */}
              <div className="flex justify-center w-full">
                <div className="flex flex-col">
                  {Array.from({ length: 24 }, (_, i) => {
                    const rowNum = i + 1;
                    // Palcos izquierdos
                    let leftPalcos;
                    if (boxRows.includes(rowNum)) {
                      leftPalcos = [1, 2].map(idx => {
                        const boxId = `BOX-${rowNum}-L${idx}`;
                        const palco = allSeats.find(s => s.id === boxId);
                        if (!palco || !palco.visible) {
                          return <div key={boxId} className="w-2.5 h-2.5 xs:w-3 xs:h-3 sm:w-3.5 sm:h-3.5 lg:w-4 lg:h-4 mx-0.5" />;
                        }
                        
                        // Determinar el estilo inline para colores específicos de palcos izquierdos
                        let palcoStyle = { display: 'block' };
                        const palcoClassName = getSeatClassName(palco);
                        
                        if (palcoClassName.includes('style-sold')) {
                          palcoStyle.backgroundColor = 'rgb(22, 101, 52)';
                        } else if (palcoClassName.includes('style-reserved')) {
                          palcoStyle.backgroundColor = 'rgb(239, 246, 255)';
                        }
                        
                        return (
                          <button
                            key={boxId}
                            onClick={() => handleSeatClick(boxId)}
                            className={palcoClassName.replace(' style-sold', '').replace(' style-reserved', '').replace(' style-blocked', '') + ' w-2.5 h-2.5 xs:w-3 xs:h-3 sm:w-3.5 sm:h-3.5 lg:w-4 lg:h-4 rounded-full border cursor-pointer transition-colors duration-200 mx-0.5 flex items-center justify-center'}
                            onMouseEnter={(e) => handleSeatMouseEnter(palco, `PB ${palco?.number}`, palcoPrice, e)}
                            onMouseMove={handleSeatMouseMove}
                            onMouseLeave={handleSeatMouseLeave}
                            style={palcoStyle}
                          >
                          </button>
                        );
                      });
                    } else {
                      leftPalcos = [
                        <div key={`empty-left-${rowNum}-1`} className="w-2.5 h-2.5 xs:w-3 xs:h-3 sm:w-3.5 sm:h-3.5 lg:w-4 lg:h-4 mx-0.5" />,
                        <div key={`empty-left-${rowNum}-2`} className="w-2.5 h-2.5 xs:w-3 xs:h-3 sm:w-3.5 sm:h-3.5 lg:w-4 lg:h-4 mx-0.5" />
                      ];
                    }
                    // Palcos derechos
                    let rightPalcos;
                    if (boxRows.includes(rowNum)) {
                      rightPalcos = [1, 2].map(idx => {
                        const boxId = `BOX-${rowNum}-R${idx}`;
                        const palco = allSeats.find(s => s.id === boxId);
                        if (!palco || !palco.visible) {
                          return <div key={boxId} className="w-2.5 h-2.5 xs:w-3 xs:h-3 sm:w-3.5 sm:h-3.5 lg:w-4 lg:h-4 mx-0.5" />;
                        }
                        
                        // Determinar el estilo inline para colores específicos de palcos derechos
                        let palcoStyle = { display: 'block' };
                        const palcoClassName = getSeatClassName(palco);
                        
                        if (palcoClassName.includes('style-sold')) {
                          palcoStyle.backgroundColor = 'rgb(22, 101, 52)';
                        } else if (palcoClassName.includes('style-reserved')) {
                          palcoStyle.backgroundColor = 'rgb(239, 246, 255)';
                        } else if (palcoClassName.includes('style-blocked')) {
                          palcoStyle.backgroundColor = 'rgb(254, 243, 199)';
                        }
                        
                        return (
                          <button
                            key={boxId}
                            onClick={() => handleSeatClick(boxId)}
                            className={palcoClassName.replace(' style-sold', '').replace(' style-reserved', '').replace(' style-blocked', '') + ' w-2.5 h-2.5 xs:w-3 xs:h-3 sm:w-3.5 sm:h-3.5 lg:w-4 lg:h-4 rounded-full border cursor-pointer transition-colors duration-200 mx-0.5 flex items-center justify-center'}
                            onMouseEnter={(e) => handleSeatMouseEnter(palco, `PB ${palco?.number}`, palcoPrice, e)}
                            onMouseMove={handleSeatMouseMove}
                            onMouseLeave={handleSeatMouseLeave}
                            style={palcoStyle}
                          >
                          </button>
                        );
                      });
                    } else {
                      rightPalcos = [
                        <div key={`empty-right-${rowNum}-3`} className="w-2.5 h-2.5 xs:w-3 xs:h-3 sm:w-3.5 sm:h-3.5 lg:w-4 lg:h-4 mx-0.5" />,
                        <div key={`empty-right-${rowNum}-4`} className="w-2.5 h-2.5 xs:w-3 xs:h-3 sm:w-3.5 sm:h-3.5 lg:w-4 lg:h-4 mx-0.5" />
                      ];
                    }
                    return (
                      <div key={`pb-row-${rowNum}`} className="flex items-center">
                        <div className="flex">{leftPalcos}</div>
                        {renderRow(rowNum, plantaBajaSeats, "planta-baja")}
                        <div className="flex">{rightPalcos}</div>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* PRIMER PISO */}
              <div className="flex justify-center">
                <div className="flex flex-col">
                  <div className="space-y-1 sm:space-y-1.5">
                    <h3 className="text-center font-bold text-gray-700 mb-3 sm:mb-4 lg:mb-6 text-sm sm:text-base lg:text-lg bg-purple-50 px-4 py-2 rounded-lg">1º PISO</h3>
                    {Array.from({ length: 17 }, (_, i) => i + 1).map((rowNum) => 
                      renderRow(rowNum, primerPisoSeats, "primer-piso")
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        
        <div className={`bg-gray-50 rounded-lg p-4 ${isMobile ? 'mt-3' : 'mt-4 sm:mt-6 lg:mt-8'}`}>
          <h4 className={`font-semibold text-gray-700 mb-3 text-center ${isMobile ? 'text-xs' : 'text-sm'}`}>Leyenda</h4>
          
          <div className={`flex flex-wrap justify-center ${isMobile ? 'gap-2' : 'gap-3 sm:gap-4'}`}>
            
            {seatTypeList && seatTypeList.length > 0 && (
              seatTypeList.map((st) => {
                const borderClass = st.color ? st.color.replace('bg-', 'border-').replace('-500', '-700').replace('-600', '-800') : 'border-gray-700';
                const formattedPrice = `$${parseFloat(st.price).toLocaleString('es-AR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
                return (
                  <div key={st.type} className={`flex items-center gap-2 bg-white rounded-lg shadow-sm ${isMobile ? 'px-2 py-1' : 'px-3 py-2'}`}>
                    <div className={`rounded ${st.color || 'bg-gray-500'} ${borderClass} ${isMobile ? 'w-3 h-3' : 'w-4 h-4'}`}></div>
                    <span className={`font-medium ${isMobile ? 'text-xs' : 'text-sm'}`}>{st.description} ({formattedPrice})</span>
                  </div>
                );
              })
            )}
            
            
            <div className={`flex items-center gap-2 bg-white rounded-lg shadow-sm ${isMobile ? 'px-2 py-1' : 'px-3 py-2'}`}>
              <div className={`bg-blue-500 border-2 border-blue-700 rounded ring-2 ring-blue-300 ${isMobile ? 'w-3 h-3' : 'w-4 h-4'}`}></div>
              <span className={`font-medium ${isMobile ? 'text-xs' : 'text-sm'}`}>Seleccionado</span>
            </div>
            <div className={`flex items-center gap-2 bg-white rounded-lg shadow-sm ${isMobile ? 'px-2 py-1' : 'px-3 py-2'}`}>
              <div 
                className={`border-2 border-green-800 rounded ${isMobile ? 'w-3 h-3' : 'w-4 h-4'}`}
                style={{ backgroundColor: 'rgb(22, 101, 52)' }}
              ></div>
              <span className={`font-medium ${isMobile ? 'text-xs' : 'text-sm'}`}>Vendido</span>
            </div>
            <div className={`flex items-center gap-2 bg-white rounded-lg shadow-sm ${isMobile ? 'px-2 py-1' : 'px-3 py-2'}`}>
              <div 
                className={`border-2 border-blue-300 rounded ${isMobile ? 'w-3 h-3' : 'w-4 h-4'}`}
                style={{ backgroundColor: 'rgb(239, 246, 255)' }}
              ></div>
              <span className={`font-medium ${isMobile ? 'text-xs' : 'text-sm'}`}>Reservado</span>
            </div>
            <div className={`flex items-center gap-2 bg-white rounded-lg shadow-sm ${isMobile ? 'px-2 py-1' : 'px-3 py-2'}`}>
              <div 
                className={`border-2 border-amber-300 rounded ${isMobile ? 'w-3 h-3' : 'w-4 h-4'}`}
                style={{ backgroundColor: 'rgb(254, 243, 199)' }}
              ></div>
              <span className={`font-medium ${isMobile ? 'text-xs' : 'text-sm'}`}>Bloqueado</span>
            </div>
            <div className={`flex items-center gap-2 bg-white rounded-lg shadow-sm ${isMobile ? 'px-2 py-1' : 'px-3 py-2'}`}>
              <div className={`bg-gray-400 border-2 border-gray-500 rounded opacity-60 ${isMobile ? 'w-3 h-3' : 'w-4 h-4'}`}></div>
              <span className={`font-medium ${isMobile ? 'text-xs' : 'text-sm'}`}>Ocupado</span>
            </div>
          </div>
        </div>
      </div>

      {/* Tooltip flotante al pasar el mouse sobre un asiento */}
      {hoveredSeat && (
        <div
          className="fixed z-50 pointer-events-none"
          style={{
            left: hoveredSeat.x,
            top: hoveredSeat.y,
            transform: 'translate(-50%, calc(-100% - 12px))',
          }}
        >
          <div className="bg-gray-900 text-white rounded-lg shadow-xl px-3 py-2 min-w-[140px] max-w-[240px] border border-gray-700">
            <div className="flex items-center justify-between gap-2">
              <span className="font-semibold text-sm">{hoveredSeat.label}</span>
              <span className="text-xs text-gray-300">
                {seatStatusLabel[hoveredSeat.seat?.status] || hoveredSeat.seat?.status}
              </span>
            </div>
            <div className="text-xs text-gray-300 mt-0.5">
              ${parseFloat(hoveredSeat.price || 0).toLocaleString('es-AR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
            </div>
            {hoveredSeat.seat?.studentName && (
              <div className="mt-1.5 pt-1.5 border-t border-gray-700">
                <div className="text-[10px] uppercase tracking-wide text-gray-400">Alumno</div>
                <div className="text-sm font-semibold text-white truncate">{hoveredSeat.seat.studentName}</div>
              </div>
            )}
          </div>
          {/* Flechita del tooltip */}
          <div
            className="w-2.5 h-2.5 bg-gray-900 border-r border-b border-gray-700 mx-auto"
            style={{ transform: 'translateY(-6px) rotate(45deg)' }}
          />
        </div>
      )}
    </div>
  );
};

export default SeatMap;